import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  styled,
  alpha,
  TextField,
  InputAdornment,
  Stack,
  Button,
  Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SEOHead from '../components/SEOHead';
import { cyber } from '../theme';

// --- Types ---
interface PublicReportItem {
  id: string;
  title: string | null;
  teaser: string | null;
  source: string | null;
  publishedDate: string | null;
  tags: string[] | null;
  cveCount: number;
}

interface PublicPaginatedReportList {
  items: PublicReportItem[];
  total: number;
  page: number;
  size: number;
}

const PAGE_SIZE = 12;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// --- Styled ---
const HeroSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  padding: theme.spacing(4, 0),
}));

const ReportCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: alpha(theme.palette.background.paper, 0.6),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: cyber.glow,
    borderColor: alpha(theme.palette.primary.main, 0.4),
  },
}));

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const PublicReportsPage: React.FC = () => {
  const [data, setData] = useState<PublicPaginatedReportList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebounce(searchTerm, 500);
  const [page, setPage] = useState(1);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
      });
      if (debouncedSearch) params.set('search', debouncedSearch);

      const res = await fetch(`${API_BASE_URL}/api/public/reports?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: PublicPaginatedReportList = await res.json();
      setData(json);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Reset to first page when the search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <>
      <SEOHead
        title="Threat Intelligence Reports"
        description="Browse the latest AI-generated threat intelligence reports: summaries, sources and CVE coverage. Sign in for the full analysis, IOCs and STIX bundles."
        canonical="/reports"
        type="website"
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <HeroSection>
          <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ ...cyber.gradientText }}>
            Threat Intelligence Reports
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 720, mx: 'auto' }}>
            Explore the latest AI-generated CTI summaries. Sign in to unlock the full analysis,
            IOCs, MITRE ATT&amp;CK mapping and STIX bundles.
          </Typography>
        </HeroSection>

        <Box sx={{ mb: 4, maxWidth: 560, mx: 'auto' }}>
          <TextField
            fullWidth
            placeholder="Search reports by title, source or tag..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && !loading && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Unable to load reports: {error}
          </Alert>
        )}

        {!loading && !error && data && data.items.length === 0 && (
          <Alert severity="info">No reports found for your search.</Alert>
        )}

        {!loading && !error && data && data.items.length > 0 && (
          <>
            <Grid container spacing={3}>
              {data.items.map((report) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={report.id}>
                  <ReportCard>
                    <CardActionArea
                      component={RouterLink}
                      to={`/report/${report.id}`}
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                          {report.source && (
                            <Chip
                              size="small"
                              icon={<RssFeedIcon />}
                              label={report.source}
                              sx={{ maxWidth: '100%' }}
                            />
                          )}
                          {report.cveCount > 0 && (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={`${report.cveCount} CVE${report.cveCount > 1 ? 's' : ''}`}
                            />
                          )}
                        </Stack>

                        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ lineHeight: 1.3 }}>
                          {report.title || 'Untitled report'}
                        </Typography>

                        {report.publishedDate && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            {formatDate(report.publishedDate)}
                          </Typography>
                        )}

                        {report.teaser && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 4,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {report.teaser}
                          </Typography>
                        )}

                        {report.tags && report.tags.length > 0 && (
                          <Stack direction="row" spacing={0.5} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                            {report.tags.slice(0, 3).map((tag) => (
                              <Chip
                                key={tag}
                                size="small"
                                icon={<LocalOfferIcon sx={{ fontSize: 14 }} />}
                                label={tag}
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                        )}
                      </CardContent>

                      <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
                        <LockOpenIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight={600}>
                          Sign in for full analysis
                        </Typography>
                        <ArrowForwardIcon sx={{ fontSize: 16, ml: 'auto' }} />
                      </Box>
                    </CardActionArea>
                  </ReportCard>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          </>
        )}

        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button
            component={RouterLink}
            to="/"
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
          >
            Sign in to access the full platform
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default PublicReportsPage;
